import { createFileRoute, notFound } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CatalogProductCard, QuickAddStates } from "@/components/storefront/catalog/catalog-product-card";
import { StorefrontProductNotFound } from "@/components/storefront/product/storefront-product-not-found";
import { StorefrontProductPage } from "@/components/storefront/product/storefront-product-page";
import { productsQueryOptions, selectStorefrontCatalog } from "@/lib/products";
import type { Product } from "@/lib/products";
import { SalesChannels } from "@/lib/storefront/channel";
import {
  canQuickAdd,
  findProductByHandle,
  getListPrice,
  getProductHandle,
  getRemainingStock,
  hasPriceRange,
  isSoldOut,
} from "@/lib/storefront/catalog-filters";
import { websiteConfigQueryOptions } from "@/lib/website-config";
import { useCartSheet } from "@/hooks/storefront/use-cart-sheet";
import { useStorefrontCatalog } from "@/hooks/storefront/use-storefront-catalog";
import { useStorefrontCart } from "@/hooks/storefront/use-storefront-cart";
import {
  AddToCartResults,
  useStorefrontAddToCart,
} from "@/hooks/storefront/use-storefront-add-to-cart";
import { useLocaleNavigation } from "@/hooks/use-locale-navigation";

const RELATED_PRODUCT_COUNT = 3;

export const Route = createFileRoute("/$locale/products/$slug")({
  loader: async ({ context, params }) => {
    const websiteConfig = await context.queryClient.ensureQueryData(
      websiteConfigQueryOptions()
    );
    // Product pages are the webshop's; the ordering site shows its menu.
    if (websiteConfig.channel !== SalesChannels.storefront) throw notFound();

    const menu = await context.queryClient.ensureQueryData(
      productsQueryOptions()
    );
    const product = findProductByHandle(
      selectStorefrontCatalog(menu),
      params.slug
    );
    if (!product) throw notFound();

    return {
      title: `${product.name} | ${websiteConfig.companyName}`,
      description: product.description
        ?.replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 160),
      image: product.images[0],
    };
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          { title: loaderData.title },
          ...(loaderData.description
            ? [{ name: "description", content: loaderData.description }]
            : []),
          { property: "og:title", content: loaderData.title },
          ...(loaderData.image
            ? [{ property: "og:image", content: loaderData.image }]
            : []),
        ]
      : [],
  }),
  component: ProductRoute,
  notFoundComponent: StorefrontProductNotFound,
});

function ProductRoute() {
  const { slug } = Route.useParams();
  const { data: categories } = useStorefrontCatalog();
  const { quantityByProductId } = useStorefrontCart();
  const setCartOpen = useCartSheet((state) => state.setOpen);

  const product = findProductByHandle(categories, slug);
  if (!product) return <StorefrontProductNotFound />;

  const category = categories.find(
    (entry) => entry.id === product.productCategoryId
  );
  const related = (category?.products ?? [])
    .filter((entry) => entry.id !== product.id && !isSoldOut(entry))
    .slice(0, RELATED_PRODUCT_COUNT);

  return (
    <StorefrontProductPage
      product={product}
      category={category ? { id: category.id, name: category.name } : undefined}
      price={getListPrice(product)}
      isFromPrice={hasPriceRange(product)}
      isSoldOut={isSoldOut(product)}
      remainingStock={getRemainingStock(
        product,
        quantityByProductId.get(product.id) ?? 0
      )}
      onAdded={() => setCartOpen(true)}
      related={<RelatedProducts products={related} />}
    />
  );
}

function RelatedProducts({ products }: { products: Product[] }) {
  const { t } = useTranslation("translation");
  const { quickAdd } = useStorefrontAddToCart();
  const { navigateWithLocale } = useLocaleNavigation();

  if (products.length === 0) return null;

  return (
    <section className="mt-16 border-t pt-10">
      <h2 className="mb-6 text-xl font-bold">
        {t("Storefront.Product.related")}
      </h2>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-6">
        {products.map((product) => (
          <li key={product.id} className="flex">
            <CatalogProductCard
              product={product}
              href={`/products/${getProductHandle(product)}`}
              price={getListPrice(product)}
              isFromPrice={hasPriceRange(product)}
              quickAddState={
                canQuickAdd(product)
                  ? QuickAddStates.add
                  : QuickAddStates.chooseOptions
              }
              onQuickAdd={async () => {
                const result = await quickAdd(product);
                if (result === AddToCartResults.needsOptions) {
                  navigateWithLocale(`/products/${getProductHandle(product)}`);
                }
              }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
